import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { GoogleGenerativeAI } from '@google/generative-ai';

export default function AIGoalSuggestions({ open, onClose, onSelect, jobFunction, careerGoal, nextRole }) {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerateSuggestion = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GOOGLE_AI_API_KEY);
      const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

      const prompt = `
        As an AI career development assistant, generate an ambitious development goal for a ${jobFunction || 'professional'}.
        
        The goal should be specific and actionable, focusing on professional growth and skill development.
        
        Context:
        - Employee goal: ${careerGoal || 'Career goal'}
        - Next role: ${nextRole || 'Next role'}

        For the suggested goal, provide three aspects:
        1. Why is this goal Thrilling: Explain why this goal is exciting and motivating
        2. Why is this goal Important: Explain why this goal matters to the individual and others
        3. Why is this goal Daunting: Explain why this goal pushes them out of their comfort zone
        
        Format the response in plain text with line breaks exactly like this, without any markdown or special characters:
        Goal: [The goal text]
        Thrilling: [Thrilling explanation]
        Important: [Important explanation]
        Daunting: [Daunting explanation]
      `;

      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      // Parse the response into structured data
      const parsedResponse = {};
      const lines = text.split('\n').filter(line => line.trim());
      
      lines.forEach(line => {
        if (line.startsWith('Goal:')) {
          parsedResponse.goal = line.replace('Goal:', '').trim();
        } else if (line.startsWith('Thrilling:')) {
          parsedResponse.thrilling = line.replace('Thrilling:', '').trim();
        } else if (line.startsWith('Important:')) {
          parsedResponse.important = line.replace('Important:', '').trim();
        } else if (line.startsWith('Daunting:')) {
          parsedResponse.daunting = line.replace('Daunting:', '').trim();
        }
      });

      // Validate that we have all required fields
      if (!parsedResponse.goal || !parsedResponse.thrilling || 
          !parsedResponse.important || !parsedResponse.daunting) {
        throw new Error('Invalid AI response format');
      }

      setSuggestion(parsedResponse);
    } catch (error) {
      console.error('AI Suggestion Error:', error);
      setError('Failed to generate suggestion. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate suggestion when dialog opens
  React.useEffect(() => {
    if (open) {
      handleGenerateSuggestion();
    } else {
      setSuggestion(null);
      setError(null);
    }
  }, [open]);

  const handleAddGoal = () => {
    if (suggestion) {
      onSelect({
        goal: suggestion.goal,
        thrillingReason: suggestion.thrilling,
        importantReason: suggestion.important,
        dauntingReason: suggestion.daunting,
        score: 0, // Initial score
        actionItems: [] // Initial empty action items
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2
      }}>
        <Typography variant="h6">AI-Suggested Development Goal</Typography>
        <Button 
          onClick={handleGenerateSuggestion}
          startIcon={<AutoAwesomeIcon />}
          disabled={loading}
          sx={{
            backgroundColor: 'grey.100',
            color: 'text.primary',
            textTransform: 'none',
            border: '1px solid transparent',
            '&:hover': {
              backgroundColor: 'common.white',
              border: '1px solid',
              borderColor: 'primary.main',
            }
          }}
        >
          Regenerate
        </Button>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : suggestion ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ pb: 4 }}>
            <Typography variant="h9" gutterBottom  sx={{ fontStyle: 'italic', color: 'grey.500' }}>
              You will be able to edit or modify all of this text after adding it.
            </Typography>
            </Box>
            <Typography variant="subtitle1" color="primary" gutterBottom sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
               Goal
              </Typography>
            <Typography variant="h6" gutterBottom>
              {suggestion.goal}
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Why is this goal Thrilling?
              </Typography>
              <Typography paragraph>{suggestion.thrilling}</Typography>

              <Typography variant="subtitle1" color="primary" gutterBottom>
                Why is this goal Important?
              </Typography>
              <Typography paragraph>{suggestion.important}</Typography>

              <Typography variant="subtitle1" color="primary" gutterBottom>
                Why is this goal Daunting?
              </Typography>
              <Typography paragraph>{suggestion.daunting}</Typography>
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleAddGoal}
          variant="contained"
          disabled={!suggestion || loading}
        >
          Add Goal
        </Button>
      </DialogActions>
    </Dialog>
  );
} 